import { Injectable } from '@angular/core';
import moment from 'moment';
import { Select } from '@ngxs/store';
import { Observable } from 'rxjs';
import { IMap } from '@nimbus-library/features/shared/models/interfaces';
import { UserPreferencesNotificationAudioService } from '@nimbus-library/features/user-settings/services/user-preferences-notification-sender/user-preferences-notification-audio.service';
import { UserPreferencesNotificationsState } from '@nimbus-library/features/user-settings/store/user-preferences-notifications.state';
import { UserPreferencesNotificationsModel } from '@nimbus-library/features/user-settings/models/user-preferences-notifications.model';

@Injectable({ providedIn: 'root' })
export abstract class BaseUserPreferencesNotificator {
  @Select(UserPreferencesNotificationsState.notificationsModel)
  protected notificationsModel$: Observable<UserPreferencesNotificationsModel>;

  private idsNotified: IMap<moment.Moment> = {};
  protected abstract audioType: string;

  protected constructor(protected readonly userPreferencesNotificationAudioService: UserPreferencesNotificationAudioService) {}

  public abstract initialize(): void;

  protected toggleAudioNotification(playAudio: boolean, audioFile: string): void {
    if (playAudio) {
      this.userPreferencesNotificationAudioService.playAudio(this.audioType, audioFile, true);
    } else {
      this.userPreferencesNotificationAudioService.stopAudio(this.audioType);
    }
  }

  /** Plays the audio file a single time (no looping); the audio element cleans itself up when it ends. */
  protected playAudioOnce(audioFile: string): void {
    this.userPreferencesNotificationAudioService.playAudio(this.audioType, audioFile, false);
  }

  protected showDesktopNotification(id: string, title: string, body: string, onClick: () => void): void {
    if (this.getIdsNotified().includes(id)) {
      return;
    }
    if (!('Notification' in window)) {
      // eslint-disable-next-line no-console
      console.log('User Notifications. This browser does not support desktop notifications.');
      return;
    }
    if (Notification.permission === 'granted') {
      this.createDesktopNotification(title, body, onClick);
      this.appendIdNotified(id);
      return;
    }
    if (Notification.permission !== 'denied') {
      Notification.requestPermission().then((permission) => {
        if (permission === 'granted') {
          this.createDesktopNotification(title, body, onClick);
          this.appendIdNotified(id);
        }
      });
    }
  }

  protected getIdsNotified(): string[] {
    return Object.keys(this.idsNotified) || [];
  }

  protected appendIdNotified(id: string): void {
    this.idsNotified[id] = moment();
  }

  protected removeIdNotified(id: string): void {
    delete this.idsNotified[id];
  }

  private createDesktopNotification(title: string, body: string, onClick: () => void): void {
    const notification = new Notification(title, {
      body,
      dir: 'auto',
      icon: `${window.location.origin}/images/shared_nimbus.png`,
      requireInteraction: true,
    });
    notification.onclick = (): void => {
      window.focus();
      onClick();
    };
    notification.onclose = (): void => {
      notification.close();
    };
  }
}
